import { CAREncoderStream, createDirectoryEncoderStream } from 'ipfs-car';
import { getApiRoot } from '~/utilities/api';

const getSHA256Hash = async (input: File) => {
  const fileAsBuffer = await input.arrayBuffer();
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', fileAsBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((item) => item.toString(16).padStart(2, '0')).join('');
};

const getPresignedUrl = async (profileId: string, filename: string) => {
  return await $fetch<{ request_token: string; issuer_did: string }>(`${getApiRoot()}/studio/presign_upload`, {
    method: 'POST',
    body: JSON.stringify({ profile_id: profileId, filename }),
  });
};

export class FilebaseUploader {
  async upload(profileId: string, files: File[]): Promise<{ filename: string; uris: string[]; hash: string }[]> {
    const filesToUpload: File[] = [];
    const filesInDirectory = [];
    for (const file of files) {
      const filename = file.name;
      const hash = await getSHA256Hash(file);
      filesToUpload.push(file);
      filesInDirectory.push({ filename, hash });
    }

    // TODO this solution is buffering all the files, to find out the root CID. Won't work for large directories
    const blocks = [];
    const writableStream = new WritableStream({
      write: (block) => blocks.push(block),
    });

    await createDirectoryEncoderStream(filesToUpload).pipeTo(writableStream);

    const rootCID = blocks.at(-1).cid;
    const blockStream = new ReadableStream({
      pull(controller) {
        if (blocks.length) {
          controller.enqueue(blocks.shift());
        } else {
          controller.close();
        }
      },
    });

    const encodedBlocks = [];
    const encodedWritableStream = new WritableStream({
      write: (block) => encodedBlocks.push(block),
    });

    await blockStream.pipeThrough(new CAREncoderStream([rootCID])).pipeTo(encodedWritableStream);

    const carBlob = new Blob(encodedBlocks, { type: 'application/car' });
    const carFile = new File([carBlob], `${rootCID}.car`);

    await $fetch(await getPresignedUrl(profileId, carFile.name), {
      method: 'PUT',
      headers: { 'x-amz-meta-import': 'car' },
      body: carFile,
    });

    return filesInDirectory.map((entry) => ({
      ...entry,
      uris: [
        `https://ipfs.mintgarden.io/ipfs/${rootCID}/${encodeURIComponent(entry.filename)}`,
        `ipfs://${rootCID}/${encodeURIComponent(entry.filename)}`,
      ],
    }));
  }
}

export function addCollectionToMetadata(metadataToUpload: any, collection: any) {
  metadataToUpload.collection = {
    id: collection.id,
    name: collection.name,
    attributes: [],
  };
  const addAttributeIfSet = (type: string, value: string | undefined) => {
    if (value) {
      metadataToUpload.collection.attributes.push({
        type,
        value,
      });
    }
  };
  addAttributeIfSet('description', collection.description);
  addAttributeIfSet('icon', collection.iconUrl);
  addAttributeIfSet('banner', collection.bannerUrl);
  addAttributeIfSet('twitter', collection.twitterHandle);
  addAttributeIfSet('website', collection.website);
  return metadataToUpload;
}
